import React, { useState, useContext, useEffect } from 'react';
import Panel from '../../../../components/Panel';
import useAPI from '../../../../hooks/useAPI';
import AuthContext from '../../../../contexts/AuthContext';
import ComponentBusy from '../../../../components/ComponentBusy';
import FlashMessage from '../../../../components/FlashMessage';
import useFlash from '../../../../hooks/useFlash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileAlt, faDownload, faEye } from '@fortawesome/free-solid-svg-icons';

export default function ContractDetails({ onLoad, onLoadEnd }) {
  const { system } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [downloadingDoc, setDownloadingDoc] = useState(null);
  const { messages, flash, remove } = useFlash();

  const handleView = (uri) => {
    const newTab = window.open(uri, '_blank');
    if (!newTab) window.location.href = uri;
  };

  const handleDownload = async (uri, label) => {
    if (downloadingDoc) return;
    setDownloadingDoc(label);
    try {
      const response = await fetch(uri);
      if (!response.ok) throw new Error(`HTTP error ${response.status}`);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `${label}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch {
      window.open(uri, '_blank');
    } finally {
      setDownloadingDoc(null);
    }
  };

  const api = useAPI();

  const load = () => {
    onLoad();
    setLoading(true);

    api.json('GET', `/systems/${system.id}/contract-summary`)
      .then(r => r.json())
      .then((r) => {
        if (r.errors) {
          const message = Array.isArray(r.errors) ? r.errors[0] : Object.values(r.errors)[0];
          flash(message, 'danger');
          return;
        }

        const docList = [];
        if (r.data?.contract_uri) {
          docList.push({ label: 'Contract', uri: r.data.contract_uri });
        }
        if (r.data?.security_addendum_uri) {
          docList.push({ label: 'Security Addendum', uri: r.data.security_addendum_uri });
        }
        if (r.data?.terms_uri) {
          docList.push({ label: 'Terms & Conditions', uri: r.data.terms_uri });
        }
        if (r.data?.summary_uri) {
          docList.push({ label: 'Agreement Summary', uri: r.data.summary_uri });
        }

        setDocuments(docList);
      })
      .catch(() => setLoading(false))
      .finally(() => {
        onLoadEnd();
        setLoading(false);
      });
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  if (loading) {
    return <ComponentBusy />;
  }

  return (
    <div className="account-settings">
      <FlashMessage messages={messages} onRemove={remove} />
      <Panel
        title="Contract Details"
        contentStyle={{ padding: 20 }}
        headerStyle={{
          borderBottomWidth: 1,
          borderStyle: 'solid',
          borderColor: '#E2E3E8',
          padding: 20,
        }}
      >
        <div className="row">
          <div className="col-12">
            {documents.length === 0 ? (
              <div style={{ display: 'flex', padding: 40, justifyContent: 'center', alignItems: 'center' }}>
                <span>No Contract Details</span>
              </div>
            ) : (
              documents.map(({ label, uri }, idx) => (
                <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: idx < documents.length - 1 ? '1px solid #E2E3E8' : 'none' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <FontAwesomeIcon icon={faFileAlt} style={{ color: '#6B7280' }} />
                    {label}
                  </span>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => handleView(uri)}
                    >
                      <FontAwesomeIcon icon={faEye} style={{ marginRight: 4 }} />
                      View
                    </button>
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => handleDownload(uri, label)}
                      disabled={downloadingDoc === label}
                    >
                      <FontAwesomeIcon icon={faDownload} style={{ marginRight: 4 }} />
                      {downloadingDoc === label ? 'Downloading...' : 'Download'}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </Panel>
    </div>
  );
}